// =============================================================================
// app/settings/security.tsx — password / 2FA / sessions.
//
// P1-PROFILE redesign:
//   - Change Password: a LIVE inline rule checklist (length + upper/lower/digit/
//     symbol + match) turns green as you type — no more guess-and-get-a-400.
//   - 2FA: real TOTP setup (QR + manual key) unchanged; the backup-code screen is
//     now a proper 2-COLUMN grid with working Copy (share sheet → Copy) and
//     Download / Save (share sheet → Save to Files) actions.
//   - Passkey row stays REMOVED (dead-end; no dead buttons).
//   - Uniform glass surfaces + tokens + full-screen ParticleBackground.
// All API/business logic (change-password, /2fa/*, /auth/sessions) is unchanged.
// =============================================================================
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  ScrollView,
  Alert,
  ActivityIndicator,
  TouchableOpacity,
  Switch,
  Modal,
  Image,
  Share,
  KeyboardAvoidingView,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '../../components/ui/Icon';
import { api } from '../../services/api';
import { GlassCard, GlassButton, GlassSurface } from '../../components/glass';
import ParticleBackground from '../../components/ui/ParticleBackground';
import { colors, spacing, radius, fonts, typographyScale, getLayout } from '../../constants/theme';

interface Session {
  id: string;
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
  isCurrent: boolean;
}

// Backend strong-password policy, expressed as user-visible rules so we can show
// a live checklist AND gate the button with the exact same predicates.
const PASSWORD_RULES: { key: string; label: string; test: (p: string) => boolean }[] = [
  { key: 'len', label: 'At least 12 characters', test: (p) => p.length >= 12 },
  { key: 'lower', label: 'A lowercase letter', test: (p) => /[a-z]/.test(p) },
  { key: 'upper', label: 'An uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { key: 'digit', label: 'A number', test: (p) => /[0-9]/.test(p) },
  { key: 'symbol', label: 'A symbol', test: (p) => /[^A-Za-z0-9]/.test(p) },
];

export default function SecurityScreen() {
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  const containerStyle = layout !== 'phone' ? { maxWidth: layout === 'desktop' ? 720 : 620, alignSelf: 'center' as const, width: '100%' as const } : undefined;
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [toggling2FA, setToggling2FA] = useState(false);
  // 2FA setup/disable flow (real TOTP: setup -> scan QR -> enter code -> enable).
  const [twoFAModal, setTwoFAModal] = useState<null | 'enable' | 'disable' | 'backup'>(null);
  const [twoFASecret, setTwoFASecret] = useState('');
  const [twoFAQr, setTwoFAQr] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFAPassword, setTwoFAPassword] = useState('');
  const [twoFABackupCodes, setTwoFABackupCodes] = useState<string[]>([]);
  const [twoFABusy, setTwoFABusy] = useState(false);
  const [sessions, setSessions] = useState<Session[]>([]);

  // Password fields
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const strongEnough = useMemo(
    () => PASSWORD_RULES.every((r) => r.test(newPassword)),
    [newPassword],
  );
  const passwordsMatch = !!confirmPassword && newPassword === confirmPassword;

  const fetchSecurity = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    // 2FA status lives at GET /2fa/status -> { data: { enabled, enabledAt } };
    // sessions at GET /auth/sessions -> { data: { sessions: [...] } }. api()
    // unwraps one level, so read res.data.enabled / res.data.sessions.
    const [statusRes, sessionsRes] = await Promise.all([
      api<{ enabled: boolean }>('/2fa/status'),
      api<{ sessions: Session[] }>('/auth/sessions'),
    ]);
    if (statusRes.success && statusRes.data) {
      setTwoFAEnabled(!!statusRes.data.enabled);
    }
    if (sessionsRes.success && sessionsRes.data) {
      setSessions(Array.isArray(sessionsRes.data.sessions) ? sessionsRes.data.sessions : []);
    } else if (!sessionsRes.success) {
      setSessions([]);
    }
    if (!statusRes.success && !sessionsRes.success) {
      setLoadError(statusRes.error || sessionsRes.error || 'Failed to load security settings.');
    }
    setLoading(false);
  }, []);

  function formatDate(value?: string): string {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return value; // not a parseable date — show as-is
    return d.toLocaleString();
  }

  useEffect(() => {
    fetchSecurity();
  }, [fetchSecurity]);

  async function handleChangePassword() {
    if (!currentPassword || !newPassword) {
      Alert.alert('Error', 'Please fill in all password fields.');
      return;
    }
    if (newPassword !== confirmPassword) {
      Alert.alert('Error', 'New passwords do not match.');
      return;
    }
    // Mirror the backend's strong-password policy so the user gets immediate
    // feedback instead of a 400 (also enforced live by the checklist above).
    if (!strongEnough) {
      Alert.alert(
        'Weak Password',
        'Password must be at least 12 characters and include uppercase, lowercase, a number, and a symbol.'
      );
      return;
    }

    setChangingPassword(true);
    // Backend expects camelCase keys (currentPassword / newPassword).
    const res = await api('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({
        currentPassword,
        newPassword,
      }),
    });
    setChangingPassword(false);

    if (res.success) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      Alert.alert('Success', 'Your password has been changed.');
    } else {
      Alert.alert('Error', res.error || 'Failed to change password.');
    }
  }

  // Enabling 2FA is a real TOTP flow: request a secret + QR from /2fa/setup,
  // have the user scan it and enter the 6-digit code, then POST /2fa/enable.
  // Disabling requires the current password + a current code via /2fa/disable.
  async function handleToggle2FA(value: boolean) {
    setTwoFACode('');
    setTwoFAPassword('');
    if (value) {
      setToggling2FA(true);
      const res = await api<{ secret: string; qrCodeDataURL: string }>('/2fa/setup', { method: 'POST' });
      setToggling2FA(false);
      if (res.success && res.data?.secret) {
        setTwoFASecret(res.data.secret);
        setTwoFAQr(res.data.qrCodeDataURL || '');
        setTwoFAModal('enable');
      } else {
        Alert.alert('Error', res.error || 'Could not start 2FA setup.');
      }
    } else {
      setTwoFAModal('disable');
    }
  }

  async function submitEnable2FA() {
    if (!/^\d{6}$/.test(twoFACode)) {
      Alert.alert('Invalid Code', 'Enter the 6-digit code from your authenticator app.');
      return;
    }
    setTwoFABusy(true);
    const res = await api<{ backupCodes: string[] }>('/2fa/enable', {
      method: 'POST',
      body: JSON.stringify({ verificationCode: twoFACode }),
    });
    setTwoFABusy(false);
    if (res.success) {
      setTwoFAEnabled(true);
      setTwoFABackupCodes(Array.isArray(res.data?.backupCodes) ? res.data!.backupCodes : []);
      setTwoFAModal('backup');
    } else {
      Alert.alert('Error', res.error || 'Could not enable 2FA. Check the code and try again.');
    }
  }

  async function submitDisable2FA() {
    if (!/^\d{6}$/.test(twoFACode)) {
      Alert.alert('Invalid Code', 'Enter the 6-digit code from your authenticator app.');
      return;
    }
    if (!twoFAPassword) {
      Alert.alert('Password Required', 'Enter your current password to disable 2FA.');
      return;
    }
    setTwoFABusy(true);
    const res = await api('/2fa/disable', {
      method: 'POST',
      body: JSON.stringify({ verificationCode: twoFACode, currentPassword: twoFAPassword }),
    });
    setTwoFABusy(false);
    if (res.success) {
      setTwoFAEnabled(false);
      setTwoFAModal(null);
      Alert.alert('2FA Disabled', 'Two-factor authentication has been turned off.');
    } else {
      Alert.alert('Error', res.error || 'Could not disable 2FA.');
    }
  }

  function closeTwoFAModal() {
    setTwoFAModal(null);
    setTwoFACode('');
    setTwoFAPassword('');
    setTwoFASecret('');
    setTwoFAQr('');
    setTwoFABackupCodes([]);
  }

  // Copy / download backup codes via the OS share sheet — no clipboard dep needed
  // (the sheet exposes "Copy" and "Save to Files"). One formatted block per line.
  async function shareBackupCodes(dialogTitle: string) {
    if (!twoFABackupCodes.length) return;
    const body = `CRYB two-factor backup codes\n\n${twoFABackupCodes.join('\n')}\n\nKeep these safe. Each code works once.`;
    try {
      await Share.share({ message: body }, { dialogTitle });
    } catch {
      // user dismissed the sheet — nothing to do
    }
  }

  async function handleRevokeSession(sessionId: string) {
    Alert.alert('Revoke Session', 'End this session?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Revoke',
        style: 'destructive',
        onPress: async () => {
          const res = await api(`/auth/sessions/${sessionId}`, {
            method: 'DELETE',
          });
          if (res.success) {
            setSessions((prev) => prev.filter((s) => s.id !== sessionId));
            Alert.alert('Done', 'Session has been revoked.');
          } else {
            Alert.alert('Error', res.error || 'Failed to revoke session.');
          }
        },
      },
    ]);
  }

  if (loading) {
    return (
      <View style={styles.screen}>
        <ParticleBackground theme="light" />
        <View style={styles.loadingWrap}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </View>
    );
  }

  if (loadError) {
    return (
      <View style={styles.screen}>
        <ParticleBackground theme="light" />
        <View style={styles.loadingWrap}>
          <Ionicons name="alert-circle-outline" size={48} color={colors.faint} />
          <Text style={styles.emptyText}>{loadError}</Text>
          <GlassButton title="Retry" onPress={fetchSecurity} variant="secondary" size="sm" />
        </View>
      </View>
    );
  }

  const canChangePassword = !changingPassword && !!currentPassword && strongEnough && passwordsMatch;

  return (
    <View style={styles.screen}>
      <ParticleBackground theme="light" />
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <ScrollView
          contentContainerStyle={[styles.scrollContent, containerStyle]}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {/* Change Password */}
          <Text style={styles.sectionTitle}>Change Password</Text>
          <GlassCard contentStyle={styles.cardStack}>
            <TextInput
              style={styles.input}
              placeholder="Current password"
              placeholderTextColor={colors.faint}
              secureTextEntry
              value={currentPassword}
              onChangeText={setCurrentPassword}
              autoCapitalize="none"
            />
            <View style={styles.inputWrap}>
              <TextInput
                style={[styles.input, styles.inputInline]}
                placeholder="New password"
                placeholderTextColor={colors.faint}
                secureTextEntry={!showNew}
                value={newPassword}
                onChangeText={setNewPassword}
                autoCapitalize="none"
              />
              <TouchableOpacity
                onPress={() => setShowNew((s) => !s)}
                style={styles.eyeBtn}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                accessibilityRole="button"
                accessibilityLabel={showNew ? 'Hide password' : 'Show password'}
              >
                <Ionicons name={showNew ? 'eye-off-outline' : 'eye-outline'} size={18} color={colors.muted} />
              </TouchableOpacity>
            </View>
            <TextInput
              style={styles.input}
              placeholder="Confirm new password"
              placeholderTextColor={colors.faint}
              secureTextEntry={!showNew}
              value={confirmPassword}
              onChangeText={setConfirmPassword}
              autoCapitalize="none"
            />

            {/* Live inline rule checklist */}
            {(newPassword.length > 0 || confirmPassword.length > 0) && (
              <View style={styles.rulesWrap}>
                {PASSWORD_RULES.map((r) => {
                  const ok = r.test(newPassword);
                  return (
                    <View key={r.key} style={styles.ruleRow}>
                      <Ionicons
                        name={ok ? 'checkmark-circle' : 'ellipse-outline'}
                        size={15}
                        color={ok ? colors.success : colors.faint}
                      />
                      <Text style={[styles.ruleText, ok && styles.ruleTextOk]}>{r.label}</Text>
                    </View>
                  );
                })}
                <View style={styles.ruleRow}>
                  <Ionicons
                    name={passwordsMatch ? 'checkmark-circle' : 'ellipse-outline'}
                    size={15}
                    color={passwordsMatch ? colors.success : colors.faint}
                  />
                  <Text style={[styles.ruleText, passwordsMatch && styles.ruleTextOk]}>Passwords match</Text>
                </View>
              </View>
            )}

            <GlassButton
              title="Update Password"
              onPress={handleChangePassword}
              loading={changingPassword}
              disabled={!canChangePassword}
              fullWidth
            />
          </GlassCard>

          {/* 2FA */}
          <Text style={styles.sectionTitle}>Two-Factor Authentication</Text>
          <GlassCard>
            <View style={styles.toggleRow}>
              <View style={styles.toggleLeft}>
                <View style={styles.iconWrap}>
                  <Ionicons name="key-outline" size={20} color={colors.primary} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={styles.toggleLabel}>Authenticator App</Text>
                  <Text style={styles.toggleDesc}>
                    {twoFAEnabled ? 'Enabled — your account is protected' : 'Add an extra layer of security'}
                  </Text>
                </View>
              </View>
              {toggling2FA ? (
                <ActivityIndicator size="small" color={colors.primary} />
              ) : (
                <Switch
                  value={!!twoFAEnabled}
                  onValueChange={handleToggle2FA}
                  trackColor={{ false: colors.faint, true: colors.primary + '60' }}
                  thumbColor={twoFAEnabled ? colors.primary : colors.card}
                  accessibilityLabel="Enable two-factor authentication"
                />
              )}
            </View>
          </GlassCard>

          {/* Passkeys: REMOVED for v1 (triage IMG_5944 — a "Set up Passkey" row
              that only alerted "future update" is a dead-end; no dead buttons.
              Re-add when a real passkey flow ships.) */}

          {/* Active Sessions */}
          <Text style={styles.sectionTitle}>Active Sessions</Text>
          <GlassCard padding={sessions.length === 0 ? spacing.lg : 0}>
            {sessions.length === 0 ? (
              <Text style={styles.emptyText}>No active sessions found.</Text>
            ) : (
              sessions.map((session, idx) => (
                <View
                  key={session.id}
                  style={[
                    styles.sessionRow,
                    idx < sessions.length - 1 && styles.sessionBorder,
                  ]}
                >
                  <View style={{ flex: 1 }}>
                    <View style={styles.sessionHeader}>
                      <Ionicons
                        name="desktop-outline"
                        size={16}
                        color={colors.ink2}
                      />
                      <Text style={styles.sessionDevice} numberOfLines={1}>
                        {session.isCurrent ? 'This device' : 'Active session'}
                      </Text>
                      {session.isCurrent && (
                        <View style={styles.currentBadge}>
                          <Text style={styles.currentBadgeText}>Current</Text>
                        </View>
                      )}
                    </View>
                    <Text style={styles.sessionMeta} numberOfLines={1}>
                      Last active {formatDate(session.updatedAt)}
                    </Text>
                    <Text style={styles.sessionMeta} numberOfLines={1}>
                      Signed in {formatDate(session.createdAt)}
                    </Text>
                  </View>
                  {!session.isCurrent && (
                    <TouchableOpacity
                      onPress={() => handleRevokeSession(session.id)}
                      activeOpacity={0.6}
                      accessibilityRole="button"
                      accessibilityLabel="Revoke this session"
                      hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                    >
                      <Ionicons name="close-circle" size={22} color={colors.error} />
                    </TouchableOpacity>
                  )}
                </View>
              ))
            )}
          </GlassCard>

          <View style={{ height: spacing.xxxl }} />
        </ScrollView>
      </KeyboardAvoidingView>

      {/* 2FA setup / disable / backup-codes modal */}
      <Modal
        visible={twoFAModal !== null}
        transparent
        animationType="fade"
        onRequestClose={closeTwoFAModal}
      >
        <View style={styles.modalOverlay}>
          <GlassSurface variant="sheet" radius={radius.xl} style={styles.modalCard} contentStyle={styles.modalContent}>
            {twoFAModal === 'enable' && (
              <ScrollView showsVerticalScrollIndicator={false}>
                <Text style={styles.modalTitle}>Enable Two-Factor Auth</Text>
                <Text style={styles.modalBody}>
                  Scan this QR code with an authenticator app (Google Authenticator, Authy, 1Password), then enter the 6-digit code it shows.
                </Text>
                {!!twoFAQr && (
                  <View style={styles.qrFrame}>
                    <Image source={{ uri: twoFAQr }} style={styles.qrImage} resizeMode="contain" />
                  </View>
                )}
                {!!twoFASecret && (
                  <View style={styles.secretBox}>
                    <Text style={styles.secretLabel}>Or enter this key manually</Text>
                    <Text style={styles.secretValue} selectable>{twoFASecret}</Text>
                  </View>
                )}
                <TextInput
                  style={styles.codeInput}
                  value={twoFACode}
                  onChangeText={(v) => setTwoFACode(v.replace(/\D/g, '').slice(0, 6))}
                  keyboardType="number-pad"
                  placeholder="123456"
                  placeholderTextColor={colors.faint}
                  maxLength={6}
                />
                <View style={styles.modalActions}>
                  <GlassButton title="Cancel" onPress={closeTwoFAModal} variant="secondary" size="md" style={{ flex: 1 }} />
                  <GlassButton title="Verify & Enable" onPress={submitEnable2FA} loading={twoFABusy} size="md" style={{ flex: 1 }} />
                </View>
              </ScrollView>
            )}

            {twoFAModal === 'disable' && (
              <View>
                <Text style={styles.modalTitle}>Disable Two-Factor Auth</Text>
                <Text style={styles.modalBody}>
                  Enter your current password and a 6-digit code from your authenticator app to turn off 2FA.
                </Text>
                <TextInput
                  style={styles.codeInput}
                  value={twoFAPassword}
                  onChangeText={setTwoFAPassword}
                  secureTextEntry
                  placeholder="Current password"
                  placeholderTextColor={colors.faint}
                  autoCapitalize="none"
                />
                <TextInput
                  style={styles.codeInput}
                  value={twoFACode}
                  onChangeText={(v) => setTwoFACode(v.replace(/\D/g, '').slice(0, 6))}
                  keyboardType="number-pad"
                  placeholder="123456"
                  placeholderTextColor={colors.faint}
                  maxLength={6}
                />
                <View style={styles.modalActions}>
                  <GlassButton title="Cancel" onPress={closeTwoFAModal} variant="secondary" size="md" style={{ flex: 1 }} />
                  <TouchableOpacity
                    style={[styles.dangerBtn, twoFABusy && styles.dangerBtnBusy]}
                    onPress={submitDisable2FA}
                    disabled={twoFABusy}
                    activeOpacity={0.8}
                    accessibilityRole="button"
                    accessibilityLabel="Disable 2FA"
                  >
                    {twoFABusy ? <ActivityIndicator size="small" color={colors.white} /> : <Text style={styles.dangerBtnText}>Disable 2FA</Text>}
                  </TouchableOpacity>
                </View>
              </View>
            )}

            {twoFAModal === 'backup' && (
              <View>
                <Text style={styles.modalTitle}>Save Your Backup Codes</Text>
                <Text style={styles.modalBody}>
                  Store these somewhere safe. Each code can be used once if you lose access to your authenticator.
                </Text>
                <View style={styles.backupGrid}>
                  {twoFABackupCodes.map((c) => (
                    <View key={c} style={styles.backupCell}>
                      <Text style={styles.backupCode} selectable>{c}</Text>
                    </View>
                  ))}
                </View>
                <View style={styles.backupActions}>
                  <GlassButton
                    title="Copy"
                    icon="copy-outline"
                    onPress={() => shareBackupCodes('Copy backup codes')}
                    variant="secondary"
                    size="md"
                    style={{ flex: 1 }}
                  />
                  <GlassButton
                    title="Download"
                    icon="download-outline"
                    onPress={() => shareBackupCodes('Save backup codes')}
                    variant="secondary"
                    size="md"
                    style={{ flex: 1 }}
                  />
                </View>
                <GlassButton title="Done" onPress={closeTwoFAModal} fullWidth style={{ marginTop: spacing.md }} />
              </View>
            )}
          </GlassSurface>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  loadingWrap: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.lg,
  },
  scrollContent: {
    padding: spacing.lg,
  },
  sectionTitle: {
    ...typographyScale.caption,
    color: colors.muted,
    marginBottom: spacing.sm,
    marginLeft: spacing.xs,
    marginTop: spacing.lg,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    ...fonts.bold,
  },
  cardStack: {
    gap: spacing.md,
  },
  input: {
    ...typographyScale.body,
    color: colors.ink,
    backgroundColor: colors.bg,
    borderRadius: radius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderWidth: 1,
    borderColor: colors.cardBorder,
    ...fonts.regular,
  },
  inputWrap: {
    position: 'relative',
    justifyContent: 'center',
  },
  inputInline: {
    paddingRight: 44,
  },
  eyeBtn: {
    position: 'absolute',
    right: spacing.md,
    height: '100%',
    justifyContent: 'center',
  },
  rulesWrap: {
    gap: spacing.xs,
    paddingVertical: spacing.xs,
  },
  ruleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  ruleText: {
    ...typographyScale.caption,
    color: colors.muted,
    ...fonts.regular,
  },
  ruleTextOk: {
    color: colors.success,
    ...fonts.medium,
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  toggleLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    flex: 1,
  },
  iconWrap: {
    width: 36,
    height: 36,
    borderRadius: radius.sm,
    backgroundColor: colors.primary + '10',
    justifyContent: 'center',
    alignItems: 'center',
  },
  toggleLabel: {
    ...typographyScale.body,
    color: colors.ink,
    ...fonts.medium,
  },
  toggleDesc: {
    ...typographyScale.caption,
    color: colors.muted,
    marginTop: 2,
    ...fonts.regular,
  },
  sessionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    gap: spacing.md,
  },
  sessionBorder: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.cardBorder,
  },
  sessionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  sessionDevice: {
    ...typographyScale.body,
    fontSize: 14,
    color: colors.ink,
    ...fonts.medium,
  },
  currentBadge: {
    backgroundColor: colors.success + '20',
    borderRadius: radius.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
  },
  currentBadgeText: {
    ...typographyScale.caption,
    fontSize: 11,
    color: colors.success,
    ...fonts.semibold,
  },
  sessionMeta: {
    ...typographyScale.caption,
    color: colors.muted,
    marginTop: 2,
    marginLeft: 24,
    ...fonts.regular,
  },
  emptyText: {
    ...typographyScale.body,
    color: colors.muted,
    textAlign: 'center',
    paddingVertical: spacing.md,
    ...fonts.regular,
  },
  // 2FA modal
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(26,26,46,0.45)',
    justifyContent: 'center',
    paddingHorizontal: spacing.lg,
  },
  modalCard: {
    width: '100%',
    maxWidth: 420,
    alignSelf: 'center',
  },
  modalContent: {
    padding: spacing.xl,
    maxHeight: '88%',
  },
  modalTitle: {
    ...typographyScale.h3,
    color: colors.ink,
    marginBottom: spacing.sm,
    ...fonts.bold,
  },
  modalBody: {
    ...typographyScale.body,
    fontSize: 14,
    color: colors.muted,
    marginBottom: spacing.md,
    ...fonts.regular,
  },
  qrFrame: {
    alignSelf: 'center',
    backgroundColor: colors.white,
    borderRadius: radius.lg,
    padding: spacing.md,
    marginVertical: spacing.md,
  },
  qrImage: {
    width: 196,
    height: 196,
  },
  secretBox: {
    backgroundColor: colors.bg,
    borderRadius: radius.md,
    padding: spacing.md,
    marginBottom: spacing.md,
  },
  secretLabel: {
    ...typographyScale.caption,
    color: colors.muted,
    marginBottom: 4,
    ...fonts.regular,
  },
  secretValue: {
    ...typographyScale.body,
    fontSize: 14,
    color: colors.ink,
    letterSpacing: 1,
    ...fonts.medium,
  },
  codeInput: {
    ...typographyScale.body,
    fontSize: 16,
    color: colors.ink,
    backgroundColor: colors.bg,
    borderRadius: radius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm + 2,
    borderWidth: 1,
    borderColor: colors.cardBorder,
    marginBottom: spacing.sm,
    ...fonts.regular,
  },
  modalActions: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  dangerBtn: {
    flex: 1,
    minHeight: 46,
    borderRadius: radius.full,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.error,
  },
  dangerBtnBusy: {
    opacity: 0.7,
  },
  dangerBtnText: {
    ...typographyScale.body,
    color: colors.white,
    ...fonts.semibold,
  },
  backupGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: spacing.md,
    marginHorizontal: -spacing.xs,
  },
  backupCell: {
    width: '50%',
    paddingHorizontal: spacing.xs,
    marginBottom: spacing.sm,
  },
  backupCode: {
    ...typographyScale.body,
    fontSize: 15,
    color: colors.ink,
    backgroundColor: colors.bg,
    borderRadius: radius.sm,
    borderWidth: 1,
    borderColor: colors.cardBorder,
    paddingVertical: spacing.sm,
    textAlign: 'center',
    letterSpacing: 1,
    ...fonts.medium,
  },
  backupActions: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
});
